import { useEffect, useState } from 'react';
import { Copy, Share2, FileStack, CalendarDays, Quote, X } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { BookCover } from './BookCover';
import { CategoryChip, ReaderAvatar } from './SummaryCard';
import { LikeButton } from './LikeButton';
import { formatDate } from '../../lib/summaries';

const useIsDesktop = () => {
  const [desk, setDesk] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const fn = (e) => setDesk(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return desk;
};

const copy = async (text, msg) => {
  try { await navigator.clipboard.writeText(text); toast.success(msg); return; } catch { /* fallback below */ }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(ta);
  if (ok) toast.success(msg); else toast.info(text);
};

export const SummaryDrawer = ({ item, onClose, onLike, onShowLikers }) => {
  const desktop = useIsDesktop();
  const share = async () => {
    const url = `${window.location.origin}/summaries/${item.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: item.title, text: `ملخص «${item.title}»`, url }); return; } catch { /* fall back to copy */ }
    }
    copy(url, 'تم نسخ رابط الملخص');
  };

  return (
    <Sheet open={!!item} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side={desktop ? 'left' : 'bottom'}
        className={`${desktop ? 'w-full sm:max-w-xl h-full' : 'h-[92vh] rounded-t-3xl'} p-0 overflow-y-auto bg-[#FCFAF6] [&>button]:hidden`}
        dir="rtl"
        data-testid="summary-drawer"
      >
        {item && (
          <div className="p-6 md:p-8 space-y-6 text-right">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-sm font-semibold text-muted-foreground">ملخص {item.reader_label}</SheetTitle>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-[#C19A6B]/10 transition-colors" aria-label="إغلاق" data-testid="summary-drawer-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="w-44 md:w-52 mx-auto"><BookCover item={item} large /></div>
            <div className="space-y-3">
              <h2 className="text-2xl md:text-3xl font-bold text-accent leading-snug break-words" data-testid="summary-drawer-title">{item.title}</h2>
              {item.author && <p className="text-muted-foreground">{item.author}</p>}
              <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                <CategoryChip name={item.category} />
                {item.total_pages > 0 && <span className="inline-flex items-center gap-1"><FileStack className="w-3.5 h-3.5" />{item.total_pages} صفحة</span>}
                <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{formatDate(item.published_at)}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-white border border-[#C19A6B]/15 p-3">
                <div className="flex items-center gap-2">
                  <ReaderAvatar item={item} size="w-9 h-9 text-sm" />
                  <div>
                    <p className="text-[11px] text-muted-foreground">{item.reader_label}</p>
                    <p className="text-sm font-semibold text-accent" data-testid="summary-drawer-reader">{item.reader_display}</p>
                  </div>
                </div>
                <LikeButton liked={item.liked_by_me} count={item.likes_count} onToggle={() => onLike(item)} onShowLikers={() => onShowLikers(item)} testId="drawer-like" />
              </div>
            </div>
            <p className="text-[18px] leading-[1.9] text-foreground whitespace-pre-line break-words" data-testid="summary-drawer-text">{item.text}</p>
            {item.quotes.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-bold text-accent">اقتباسات</h3>
                {item.quotes.map((q, i) => (
                  <blockquote key={i} className="relative rounded-2xl bg-[#C19A6B]/8 border-r-4 border-[#C19A6B] p-5 pt-7 text-[#5D4037] leading-[1.9]" data-testid={`summary-quote-${i}`}>
                    <Quote className="absolute top-2 left-3 w-8 h-8 text-[#C19A6B]/30" aria-hidden="true" />
                    {q}
                    <button onClick={() => copy(q, 'تم نسخ الاقتباس')} className="mt-2 flex items-center gap-1 text-[11px] text-[#A07A4E] hover:text-[#5D4037] transition-colors" data-testid={`summary-quote-copy-${i}`}>
                      <Copy className="w-3.5 h-3.5" /> نسخ
                    </button>
                  </blockquote>
                ))}
              </div>
            )}
            <div className="flex gap-3 pt-4 border-t border-[#C19A6B]/15">
              <button onClick={share} className="flex-1 h-11 rounded-xl bg-[#C19A6B] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#A9845A] transition-colors duration-200" data-testid="summary-share-btn">
                <Share2 className="w-4 h-4" /> مشاركة
              </button>
              <button onClick={() => copy(item.text, 'تم نسخ الملخص')} className="h-11 px-4 rounded-xl border border-[#C19A6B]/30 text-[#5D4037] font-semibold flex items-center gap-2 hover:bg-[#C19A6B]/10 transition-colors duration-200" data-testid="summary-copy-btn">
                <Copy className="w-4 h-4" /> نسخ الملخص
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
